import { useState, type FormEvent, type ReactElement } from 'react';
import { Link } from 'react-router-dom';

import { Notice } from '@/components/admin/Notice';
import { requestPasswordReset } from '@/features/auth/api';
import { ApiError } from '@/lib/api/http';

/**
 * パスワードを忘れた場合の再設定依頼（D-054）。
 * アカウントの有無を画面から判別できないよう、送信後は常に同じ案内を出す。
 */
export function PasswordResetRequestPage(): ReactElement {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await requestPasswordReset(email);
      setSent(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '送信できませんでした。');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-wpcanvas px-4">
      <div className="w-full max-w-sm">
        <h1 className="mb-1 text-center text-xl font-semibold text-wptext-strong">CXO GROWTH</h1>
        <p className="mb-6 text-center text-wptext-muted">パスワードの再設定</p>

        <div className="rounded-sm border border-wpborder bg-white px-5 py-6 shadow-sm">
          {error && (
            <Notice kind="error" dismissible={false}>
              {error}
            </Notice>
          )}

          {sent ? (
            <>
              <Notice kind="success" dismissible={false}>
                再設定用のメールを送信しました。
              </Notice>
              <p className="mt-3 text-wptext">
                入力されたメールアドレスが登録されていれば、パスワード再設定のご案内が届きます。
                メール内のリンクから新しいパスワードを設定してください。
              </p>
              <p className="mt-2 text-xs text-wptext-muted">
                リンクの有効期限は60分です。メールが届かない場合は、迷惑メールフォルダをご確認いただくか、
                事務局にお問い合わせください。
              </p>
            </>
          ) : (
            <form onSubmit={(event) => void onSubmit(event)}>
              <p className="mb-4 text-wptext">
                ご登録のメールアドレスを入力してください。パスワード再設定用のリンクをお送りします。
              </p>
              <label htmlFor="reset-email" className="mb-1.5 block text-wptext-muted">
                メールアドレス
              </label>
              <input
                id="reset-email"
                name="email"
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                className="mb-5 w-full rounded-sm border border-wpborder px-3 py-2"
              />
              <button
                type="submit"
                disabled={busy}
                className="w-full rounded-sm bg-wpaccent px-3 py-2 text-white hover:bg-wpaccent-hover disabled:opacity-50"
              >
                {busy ? '送信中…' : '再設定メールを送る'}
              </button>
            </form>
          )}
        </div>

        <p className="mt-4 text-center text-xs text-wptext-muted">
          <Link to="/login" className="text-wpaccent hover:underline">
            ← ログイン画面に戻る
          </Link>
        </p>
        <p className="mt-2 text-center text-xs text-wptext-muted">
          事務局・スーパー管理者はGoogleログインのため、この画面は使いません。
        </p>
      </div>
    </div>
  );
}
